import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withMetadata,
    withSearchFilter,
} from '@pf/shared-utility';
import {
    ReceiptHeaderCreate,
    ReceiptHeaderDto,
    ReceiptHeaderRead,
    ReceiptHeaderSearchParams,
} from '../../entities/receipt-header.dto';
import {
    CustomFieldMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { withWarehouseSearchFilter } from '../wms-mapping.util';
import { ReceiptLineMetadataMap } from '../receipt-lines/receipt-line.mapping-profile';

const symbols = entitySymbols(PFWmsEntities.ReceiptHeader);

withDefaultEntityMetadata<ReceiptHeaderDto>(symbols.dto, {
    dateReceived: String,
    status: String,
    receivedByUser: String,
    modifiedByUser: String,
    loadReferenceNumber: String,
    poNumber: String,
    billOfLading: String,
    trailerNumber: String,
    asnHeaderId: String,
    receiptLines: [ReceiptLineMetadataMap.symbols.dto],
    resourceAuth: ResourceAuthSymbol,
    externalReferenceId: String,
    warehouse: Object,
    carrier: Object,
    vendor: Object,
    loadId: String,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    isReceived: Boolean,
});

withDefaultEntityMetadata<ReceiptHeaderRead>(symbols.entity, {
    dateReceived: String,
    status: String,
    receivedByUser: String,
    modifiedByUser: String,
    loadReferenceNumber: String,
    poNumber: String,
    billOfLading: String,
    trailerNumber: String,
    asnHeaderId: String,
    receiptLines: [ReceiptLineMetadataMap.symbols.entity],
    resourceAuth: ResourceAuthSymbol,
    externalReferenceId: String,
    warehouse: Object,
    carrier: Object,
    vendor: Object,
    loadId: String,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    isReceived: Boolean,
});

withMetadata<ReceiptHeaderCreate>(symbols.createBody, {
    asnHeaderId: String,
    billOfLading: String,
    carrierId: String,
    dateReceived: String,
    externalReferenceId: String,
    loadId: String,
    loadReferenceNumber: String,
    modifiedByUser: String,
    poNumber: String,
    receivedByUser: String,
    resourceAuth: ResourceAuthSymbol,
    status: String,
    trailerNumber: String,
    vendorId: String,
    warehouseId: String,
});

withDefaultSearchParamsMetadata<ReceiptHeaderSearchParams>(
    symbols.searchParams,
    {
        billOfLading: String,
        poNumber: String,
        trailerNumber: String,
        asnHeaderId: String,
        externalReferenceId: String,
        warehouseId: String,
    }
);

export const ReceiptHeaderMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<ReceiptHeaderRead, ReceiptHeaderDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<ReceiptHeaderDto, ReceiptHeaderRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<ReceiptHeaderDto, ReceiptHeaderCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.warehouseId,
                mapFrom(s => s.warehouse?.id)
            ),
            forMember(
                d => d.vendorId,
                mapFrom(s => s.vendor?.id)
            )
        );
        createMap<IQueryParams<ReceiptHeaderDto>, ReceiptHeaderSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('billOfLading'),
            withSearchFilter('poNumber'),
            withSearchFilter('trailerNumber'),
            withSearchFilter('asnHeaderId'),
            withSearchFilter('externalReferenceId'),
            withWarehouseSearchFilter(),
            ...withDefaultSearchParamsMappingConfiguration()
        );
    },
};
